import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';
import { PagedResult, PatientFilter } from '../../models/pagination.model';

@Component({
  selector: 'app-patients',
  templateUrl: './patients.component.html',
  styleUrls: ['./patients.component.scss']
})
export class PatientsComponent implements OnInit, OnDestroy {

  professionalId   = 0;
  professionalName = '';
  result!: PagedResult<any>;
  loading = false;

  filter: PatientFilter = {
    page: 1, pageSize: 20,
    search: '', sortBy: 'name', sortDesc: false, isActive: true
  };

  // Modal
  showModal  = false;
  saving     = false;
  editingId: number | null = null;
  form: any  = {};

  // Datepicker - BirthDate
  showBirthDatePicker = false;
  birthPickerCurrentDate = new Date();
  birthPickerDays: Date[] = [];
  birthPickerTop = '0px';
  birthPickerLeft = '0px';
  birthDateDisplay = ''; // dd/MM/yyyy format
  weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  @ViewChild('birthDateInput', { read: ElementRef }) birthDateInput?: ElementRef;

  private searchInput$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private profState: ProfessionalStateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.searchInput$.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.filter.search = term;
      this.filter.page   = 1;
      this.load();
    });

    this.subs.add(
      this.profState.selected$.subscribe(p => {
        if (p) {
          this.professionalId   = p.id;
          this.professionalName = p.name;
          this.filter.page      = 1;
          this.load();
        }
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  load(): void {
    if (!this.professionalId) return;
    this.loading = true;
    this.api.getPatients(this.professionalId, this.filter).subscribe({
      next:  r  => { this.result = r; this.loading = false; },
      error: () => { this.loading = false; }
    });
  }

  // ── Modal ──────────────────────────────────────────────────────
  get emailsMatch(): boolean {
    return !this.form.email || this.form.email === this.form.emailConfirm;
  }

  openModal(patient?: any): void {
    this.editingId = patient?.id ?? null;
    this.form = patient
      ? { name: patient.name, email: patient.email, emailConfirm: patient.email || '',
          phone: patient.phone ? this.formatPhone(patient.phone) : '',
          birthDate: patient.birthDate ? patient.birthDate.substring(0, 10) : '',
          cpf: patient.cpf ? this.formatCpf(patient.cpf) : '', notes: patient.notes,
          address: patient.address || '', number: patient.number || '',
          neighborhood: patient.neighborhood || '', complement: patient.complement || '',
          city: patient.city || '', state: patient.state || '', zipCode: patient.zipCode || '' }
      : { name: '', email: '', emailConfirm: '', phone: '', birthDate: '', cpf: '', notes: '',
          address: '', number: '', neighborhood: '', complement: '', city: '', state: '', zipCode: '',
          sendWhatsAppConfirmation: false };

    // Initialize birthDateDisplay
    if (this.form.birthDate) {
      const parts = this.form.birthDate.split('-');
      if (parts.length === 3) {
        this.birthDateDisplay = `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    } else {
      this.birthDateDisplay = '';
    }

    this.showModal = true;
  }

  closeModal(): void { this.showModal = false; }

  save(): void {
    if (!this.form.name?.trim()) { this.notification.warning('Campo obrigatório', 'Nome é obrigatório'); return; }
    if (!this.form.phone?.trim()) { this.notification.warning('Campo obrigatório', 'Telefone é obrigatório'); return; }

    this.saving = true;
    const req = { ...this.form };
    if (!req.birthDate) delete req.birthDate;
    delete req.emailConfirm;

    if (!this.editingId) {
      req.professionalId            = this.professionalId;
      req.sendWhatsAppConfirmation  = this.form.sendWhatsAppConfirmation ?? false;
      req.professionalName          = this.professionalName;
    }

    const op = this.editingId
      ? this.api.updatePatient(this.professionalId, this.editingId, req)
      : this.api.createPatient(this.professionalId, req);

    op.subscribe({
      next: () => { this.saving = false; this.closeModal(); this.load(); },
      error: () => { this.saving = false; this.notification.error('Erro', 'Erro ao salvar paciente'); }
    });
  }

  delete(patient: any): void {
    if (!confirm(`Excluir ${patient.name}?`)) return;
    this.api.deletePatient(this.professionalId, patient.id).subscribe({
      next:  () => this.load(),
      error: (err) => {
        const msg = err?.error?.message ?? 'Erro ao excluir paciente';
        this.notification.error('Não foi possível excluir', msg);
      }
    });
  }

  // ── Filtros e ordenação ────────────────────────────────────────
  onPageChange(page: number): void  { this.filter.page = page; this.load(); }
  onSizeChange(size: number): void  { this.filter.pageSize = size; this.filter.page = 1; this.load(); }
  onSearch(term: string): void      { this.searchInput$.next(term); }

  sortBy(col: string): void {
    if (this.filter.sortBy === col) {
      this.filter.sortDesc = !this.filter.sortDesc;
    } else {
      this.filter.sortBy   = col;
      this.filter.sortDesc = false;
    }
    this.filter.page = 1;
    this.load();
  }

  sortIcon(col: string): string {
    if (this.filter.sortBy !== col) return 'bi-arrow-down-up text-muted';
    return this.filter.sortDesc ? 'bi-sort-down' : 'bi-sort-up';
  }

  // ═══════════════════════════════════════════════════════════════
  // DATEPICKER - BIRTH DATE
  // ═══════════════════════════════════════════════════════════════

  toggleBirthDatePicker(): void {
    this.showBirthDatePicker = !this.showBirthDatePicker;
    if (this.showBirthDatePicker) {
      if (this.form.birthDate) {
        const parts = this.birthDateDisplay.split('/');
        if (parts.length === 3) {
          this.birthPickerCurrentDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      } else {
        this.birthPickerCurrentDate = new Date();
      }
      this.generateBirthPickerCalendar();

      setTimeout(() => {
        if (this.birthDateInput) {
          const rect = this.birthDateInput.nativeElement.getBoundingClientRect();
          this.birthPickerTop = `${rect.bottom + 8}px`;
          this.birthPickerLeft = `${rect.left}px`;
        }
      }, 0);
    }
  }

  generateBirthPickerCalendar(): void {
    const year = this.birthPickerCurrentDate.getFullYear();
    const month = this.birthPickerCurrentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days: Date[] = [];

    for (let i = startDay - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push(new Date(year, month + 1, d));
    }
    this.birthPickerDays = days;
  }

  birthPickerPreviousMonth(): void {
    this.birthPickerCurrentDate = new Date(
      this.birthPickerCurrentDate.getFullYear(),
      this.birthPickerCurrentDate.getMonth() - 1,
      1
    );
    this.generateBirthPickerCalendar();
  }

  birthPickerNextMonth(): void {
    this.birthPickerCurrentDate = new Date(
      this.birthPickerCurrentDate.getFullYear(),
      this.birthPickerCurrentDate.getMonth() + 1,
      1
    );
    this.generateBirthPickerCalendar();
  }

  getBirthPickerMonthYear(): string {
    return this.birthPickerCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  birthPickerIsCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.birthPickerCurrentDate.getMonth();
  }

  birthPickerIsToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  birthPickerIsSelected(date: Date): boolean {
    if (!this.form.birthDate) return false;
    const parts = this.birthDateDisplay.split('/');
    if (parts.length !== 3) return false;
    return date.getDate() === parseInt(parts[0]) &&
           date.getMonth() === parseInt(parts[1]) - 1 &&
           date.getFullYear() === parseInt(parts[2]);
  }

  birthPickerSelectDate(date: Date): void {
    const pad = (n: number) => String(n).padStart(2, '0');
    this.form.birthDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    this.birthDateDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    this.showBirthDatePicker = false;
  }

  birthPickerSelectToday(): void {
    this.birthPickerSelectDate(new Date());
  }

  // ── Máscaras ────────────────────────────────────────────────────
  formatPhone(value: string): string {
    const v = value.replace(/\D/g, '').substring(0, 11);
    if (v.length === 0) return '';
    if (v.length <= 2)  return `(${v}`;
    if (v.length <= 6)  return `(${v.substring(0, 2)}) ${v.substring(2)}`;
    if (v.length <= 10) return `(${v.substring(0, 2)}) ${v.substring(2, 6)}-${v.substring(6)}`;
    return `(${v.substring(0, 2)}) ${v.substring(2, 7)}-${v.substring(7)}`;
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatPhone(input.value);
    this.form.phone = formatted;
    input.value = formatted;
  }

  formatCpf(value: string): string {
    const v = value.replace(/\D/g, '').substring(0, 11);
    if (v.length > 9) return v.substring(0, 3) + '.' + v.substring(3, 6) + '.' + v.substring(6, 9) + '-' + v.substring(9);
    if (v.length > 6) return v.substring(0, 3) + '.' + v.substring(3, 6) + '.' + v.substring(6);
    if (v.length > 3) return v.substring(0, 3) + '.' + v.substring(3);
    return v;
  }

  onCpfInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatCpf(input.value);
    this.form.cpf = formatted;
    input.value = formatted;
  }

  // ── WhatsApp status ────────────────────────────────────────────
  waStatusLabel(status: number): string {
    switch (status) {
      case 1: return 'Enviado';
      case 2: return 'Confirmado';
      case 3: return 'Não Confirmado';
      default: return 'Não Enviado';
    }
  }

  waStatusClass(status: number): string {
    switch (status) {
      case 1: return 'badge bg-info text-dark';
      case 2: return 'badge bg-success';
      case 3: return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }

  // ── Formatação manual de data ──────────────────────────────────
  onBirthDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Remove tudo que não é número

    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    this.birthDateDisplay = value;

    // Se a data está completa (10 caracteres: dd/MM/yyyy), valida e converte
    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const year = parseInt(parts[2]);

      // Validação básica
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const pad = (n: number) => String(n).padStart(2, '0');
        this.form.birthDate = `${year}-${pad(month)}-${pad(day)}`;
      }
    }
  }
}
